import { UNAVAILABLE_STATUSES } from "../../lib/booking-status";
import { getDatesInRange, getDatesInYear, parseUtcDate } from "./date-utils";
import type { BookingResponse, ConsumptionResponse } from "./types";

export type DashboardMonthlyRow = {
  month: number;
  monthLabel: string;
  bookedDays: number;
  revenueDkk: number;
};

export const buildDashboardMonthlyRows = (
  bookingsData: BookingResponse,
  locale: "da" | "en",
): DashboardMonthlyRow[] => {
  const unavailableDates = new Set<string>();
  const unavailableBookings = bookingsData.bookings.filter(
    (booking) => booking.status && UNAVAILABLE_STATUSES.has(booking.status),
  );

  unavailableBookings.forEach((booking) => {
    getDatesInRange(booking.startDate, booking.endDate).forEach((date) => unavailableDates.add(date));
  });

  const bookedByMonth = Array.from({ length: 12 }, () => 0);
  getDatesInYear(bookingsData.year).forEach((date) => {
    if (unavailableDates.has(date.toISOString().slice(0, 10))) {
      bookedByMonth[date.getUTCMonth()] += 1;
    }
  });

  const revenueByMonth = Array.from({ length: 12 }, () => 0);
  unavailableBookings.forEach((booking) => {
    if (typeof booking.amount !== "number") {
      return;
    }
    const bookingDates = getDatesInRange(booking.startDate, booking.endDate);
    if (bookingDates.length === 0) {
      return;
    }
    const amountPerDay = booking.amount / bookingDates.length;
    for (const dateValue of bookingDates) {
      const month = parseUtcDate(dateValue).getUTCMonth();
      revenueByMonth[month] += amountPerDay;
    }
  });

  const monthFormatter = new Intl.DateTimeFormat(locale === "da" ? "da-DK" : "en-US", {
    month: "short",
  });

  return Array.from({ length: 12 }, (_, month): DashboardMonthlyRow => ({
    month,
    monthLabel: monthFormatter.format(new Date(Date.UTC(bookingsData.year, month, 1))),
    bookedDays: bookedByMonth[month],
    revenueDkk: revenueByMonth[month],
  }));
};

export type DashboardDeviationRow = ConsumptionResponse["comparison"]["rows"][number];

export const summarizeNegativeDeviations = (
  rows: DashboardDeviationRow[],
  thresholdDkk: number,
) => {
  const filteredRows = rows
    .filter((row) => typeof row.deviationDkk === "number" && row.deviationDkk < thresholdDkk)
    .sort((left, right) => (left.deviationDkk ?? 0) - (right.deviationDkk ?? 0));
  const totals = filteredRows.reduce(
    (accumulator, row) => ({
      paidForKwh: accumulator.paidForKwh + row.paidForKwh,
      eloverblikKwh: accumulator.eloverblikKwh + row.eloverblikKwh,
      deviationDkk: accumulator.deviationDkk + (row.deviationDkk ?? 0),
    }),
    { paidForKwh: 0, eloverblikKwh: 0, deviationDkk: 0 },
  );
  return { filteredRows, totals };
};
